import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCompany } from "../../Context/CompanyContext";
import { fetchWithAuth } from "../../Context/UserContext";
import API_URL from "../../config";
import { Card, CardContent } from "../../components/ui/card";

const StatTile = ({ label, value }) => (
  <Card>
    <CardContent className="pt-6">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { companies } = useCompany();
  const id_company = companies?.company?.id_company;
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id_company) return;
    const fetchStats = async () => {
      try {
        const response = await fetchWithAuth(`${API_URL}/bs/company/${id_company}/dashboard-stats`);
        if (response.ok) {
          setStats(await response.json());
        } else {
          const data = await response.json();
          setError(data.error || "No se pudieron cargar las métricas.");
        }
      } catch (err) {
        console.error("Error al cargar el dashboard:", err);
        setError("Error en la conexión con el servidor.");
      }
    };
    fetchStats();
  }, [id_company]);

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }

  if (!stats) {
    return <p className="text-muted-foreground">Cargando métricas...</p>;
  }

  const enLimiteLicitaciones =
    stats.limite_licitaciones_activas !== null &&
    stats.licitaciones_activas >= stats.limite_licitaciones_activas;
  const enLimiteUsuarios =
    stats.limite_usuarios !== null && stats.usuarios_activos >= stats.limite_usuarios;

  return (
    <div>
      <h3 className="text-lg font-semibold text-foreground">Dashboard</h3>
      <p className="text-sm text-muted-foreground">
        Vista consolidada de las licitaciones y el uso de tu equipo en Procu.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Plan actual" value={stats.plan} />
        <StatTile
          label="Licitaciones activas"
          value={`${stats.licitaciones_activas} / ${stats.limite_licitaciones_activas ?? "Ilimitado"}`}
        />
        <StatTile
          label="Usuarios activos"
          value={`${stats.usuarios_activos} / ${stats.limite_usuarios ?? "Ilimitado"}`}
        />
        <StatTile label="Postulaciones recibidas" value={stats.postulaciones_totales} />
        <StatTile label="Licitaciones totales" value={stats.licitaciones_totales} />
        <StatTile label="Horas ahorradas (estimado)" value={stats.tiempo_ahorrado_horas} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{stats.tiempo_ahorrado_nota}</p>

      {(enLimiteLicitaciones || enLimiteUsuarios) && (
        <p className="mt-3 text-sm text-primary">
          Llegaste al límite de tu plan {stats.plan}.{" "}
          <Link to="/precios" className="font-medium underline">
            Ver planes disponibles
          </Link>
        </p>
      )}

      <div className="mt-6">
        <h4 className="text-sm font-semibold text-foreground">Uso por usuario</h4>
        <div className="mt-2 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-2 font-medium">Usuario</th>
                <th className="px-4 py-2 font-medium">Rol</th>
                <th className="px-4 py-2 font-medium">Licitaciones creadas</th>
              </tr>
            </thead>
            <tbody>
              {stats.uso_por_usuario.map((miembro) => (
                <tr key={miembro.id_user} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-foreground">
                    {miembro.nombre} {miembro.apellido}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{miembro.tipo_cuenta}</td>
                  <td className="px-4 py-2 text-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {miembro.licitaciones_creadas}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
